import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { BreakpointMap, getBreakpointIcon, getBreakpointStyle } from "../../../common/breakpoint"
import { RUN_MODE, toHex } from "../../../common/utility"
import { getPreferenceByString, getPreferenceDebugTabLeftWidth, setPreferenceBreakpoints } from "../../localstorage"
import { handleGetRunMode, handleGetState6502, handleGetBreakpoints, handleGetHeatMapCPU, handleGetHeatMapCPUMax } from "../../main2worker"
import {getDisassembly, 
  setLinesVisible,
  ensureDisassemblyContainsAddress,
  getDisassemblyAddressStart,
  checkDisassemblyNeedUpdatePC,
  getHighlightedAddress} from "./disassembly_utilities"
import { getChromacodedLine } from "./disassemblyview_singleline"
import React, { useEffect, useRef } from "react"
import { useGlobalContext } from "../../globalcontext"
import { useTranslation } from "../../../i18n/useTranslation"
import { PaletteName } from "viridis"
import { getViridisColorsRGB } from "../../ui_utilities"

type DisassemblyDivProps = {
  disassemblyRef: React.RefObject<HTMLDivElement | null>,
  hideFakePoint: () => void,
  setAllowScrollEvent: (value: boolean) => void,
  refresh: () => void,
  height: number
}

const DisassemblyDiv = (props: DisassemblyDivProps) => {
  const { t } = useTranslation()
  if (handleGetRunMode() !== RUN_MODE.PAUSED) {
    return <div className="noselect" style={{ marginTop: "30px", width: "24em" }}>{t("debug.pauseForDisassembly")}</div>
  }

  return <PausedDisassemblyDiv {...props} />
}

const PausedDisassemblyDiv = (props: DisassemblyDivProps) => {
  const { updateBreakpoint, setUpdateBreakpoint, setMemdumpAddress } = useGlobalContext()
  // const { refresh } = props
  const { t } = useTranslation()
  const scrollToRef = useRef<HTMLDivElement>(null)
  const lineHeightPx = 10 * (96 / 72)
  const nlines = Math.max(15, Math.floor(props.height / lineHeightPx + 0.5))
  setLinesVisible(nlines)

  const getAddress = (line: string) => {
    return parseInt(line, 16)//.slice(0, line.indexOf(":")), 16)
  }

  // This function gets used in disassemblyview_singleline but we
  // define it here so it can access our local variables.
  const onJumpClick = (addr: number) => {
    ensureDisassemblyContainsAddress(addr, true, true)
    props.refresh()
  }

  const onMemoryClick = (addr: number) => {
    setMemdumpAddress(addr)
    props.refresh()
  }

  const handleBreakpointClick = (event: React.MouseEvent<SVGSVGElement>) => {
    event.stopPropagation()
    const addr = parseInt(event.currentTarget.getAttribute("data-key") || "-1")
    const breakpoints = new BreakpointMap(handleGetBreakpoints())
    const bp = breakpoints.get(addr)
    if (bp) {
      if (bp.disabled) {
        bp.disabled = false
      } else {
        breakpoints.delete(addr)
      }
      setPreferenceBreakpoints(breakpoints)
      setUpdateBreakpoint(updateBreakpoint + 1)
    }
    props.hideFakePoint()
  }

  checkDisassemblyNeedUpdatePC()
  const disassemblyAddressStart = getDisassemblyAddressStart()
  const disArray = getDisassembly(disassemblyAddressStart, disassemblyAddressStart + 4 * nlines)
  const hasDisassembly = disArray.length > 1

  useEffect(() => {
    if (!hasDisassembly) return
    const frame = requestAnimationFrame(() => {
      if (props.disassemblyRef?.current && scrollToRef.current) {
        const container = props.disassemblyRef.current
        const line = scrollToRef.current
        props.setAllowScrollEvent(false)
        container.scrollTop += line.getBoundingClientRect().top - container.getBoundingClientRect().top
      }
    })
    return () => cancelAnimationFrame(frame)
  // The line ref points at the first decoded instruction for this address start.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disassemblyAddressStart, hasDisassembly])

  if (!hasDisassembly) {
    return <div
    style={{
      position: "relative",
      width: "24em",
      top: "0px",
      height: `${nlines * 10 - 2}pt`,
    }}>
  </div>
  }

  // Put the breakpoints into an easier to digest array format.
  const bp: Array<Breakpoint> = []
  const breakpoints = handleGetBreakpoints()
  for (let i = 0; i < nlines; i++) {
    const bp1 = breakpoints.get(getAddress(disArray[i]))
    if (bp1 && !bp1.hidden) {
      bp[i] = bp1
    }
  }

  // Calculate approximate width in characters
  // For 7pt monospace at line-height 10pt: char width ≈ 0.6 * height = 0.6 * 9.33px ≈ 5.6px
  const containerWidth = Math.max(getPreferenceDebugTabLeftWidth(), 260) - 36
  const width = Math.max(Math.floor((containerWidth / 5.6 + 2) / 2), 24)
  
  const disBefore = getDisassembly(disassemblyAddressStart - 4 * nlines, disassemblyAddressStart - 1)
  const lineTop = (disBefore[0] !== "") ? getAddress(disBefore[0]) : 0
  const topHalf = Array.from({ length: Math.floor(lineTop / 16) }, (_, i) => toHex(i * 16, 4))
  for (let i = parseInt(topHalf[topHalf.length - 1], 16) + 1; i < lineTop; i++) {
    topHalf.push(toHex(i, 4))
  }
  topHalf.push(...disBefore.map(line => line.slice(0, 16) + " ".repeat(width - 15) + line.slice(16)))

  const lineBottom = (disArray[nlines - 1] !== "") ? getAddress(disArray[nlines - 1]) : 65535
  const denseEnd = Math.min(lineBottom + 100, 65450)
  const bottomHalf: number[] = []
  for (let address = lineBottom + 1; address <= denseEnd; address++) {
    bottomHalf.push(address)
  }
  const strideStart = Math.max(lineBottom + 1, denseEnd + 1)
  for (let address = strideStart; address < 65450; address += 16) {
    bottomHalf.push(address)
  }
  for (let address = Math.max(65450, lineBottom + 1); address <= 65535; address++) {
    bottomHalf.push(address)
  }

  const pc1 = handleGetState6502().PC
  const highlightedAddress = getHighlightedAddress()
  const classLine = (line: string) => {
    const addr = getAddress(line)
    return addr === pc1 ? "program-counter" : addr === highlightedAddress ? "highlight-address" : ""
  }

  const heatMap = handleGetHeatMapCPU()
  const heatMapMax = handleGetHeatMapCPUMax().value
  const heatMapPalette = getPreferenceByString("heatMapColorTable", "Spectral") as PaletteName
  const colorTable = getViridisColorsRGB(heatMapPalette, 16)
  
  return <div style={{ width: "24em", lineHeight: "10pt" }}>
    {topHalf.map((line, i) => (<div key={`before-${i}`}>{line}</div>))}
    {disArray.map((line, index) => (
      <div key={`addr-${index}`}
        ref={index === 0 ? scrollToRef : null}
        style={{ position: "relative" }}
        className={classLine(line)}>
        {(bp[index] && !bp[index].basic &&
          <FontAwesomeIcon icon={getBreakpointIcon(bp[index])}
            className={"breakpoint-position " + getBreakpointStyle(bp[index])}
            data-key={bp[index].address}
            onClick={handleBreakpointClick} />)}
        {getChromacodedLine(line, width, onJumpClick, onMemoryClick, t, heatMap, heatMapMax, colorTable)}
      </div>
    ))}
    {bottomHalf.map((line, i) => (<div key={`after-${i}`}>{toHex(line, 4)}</div>))}
  </div>
}

export default DisassemblyDiv
